// The places a pane has shown, for going back and forward like in a browser.
// Going somewhere new drops what was ahead.

// What a pane can do in its history. A function is there only when there is
// a place to go to.
export interface PaneNav {
  goback?: () => void;
  goforward?: () => void;
}

// Alt + Left and Alt + Right go back and forward. Returns true if the key
// was one of them.
export function paneNavKey(ev: KeyboardEvent, nav: PaneNav | undefined): boolean {
  if (!ev.altKey || ev.ctrlKey || ev.metaKey || ev.shiftKey) {
    return false;
  }
  if (ev.key != "ArrowLeft" && ev.key != "ArrowRight") {
    return false;
  }
  // not the browser's own back and forward
  ev.preventDefault();
  ev.stopPropagation();
  if (ev.key == "ArrowLeft") {
    nav?.goback?.();
  } else {
    nav?.goforward?.();
  }
  return true;
}

export interface NavHistory<T> {
  entries: T[];
  // index of the place shown now
  idx: number;
}

const maxEntries = 200;

export function navNew<T>(first: T): NavHistory<T> {
  return { entries: [first], idx: 0 };
}

// Adds a place after the current one. A place equal to the current one is
// not added: that is how arriving somewhere by going back does not count as
// going somewhere new.
export function navPush<T>(h: NavHistory<T>, loc: T, eq: (a: T, b: T) => boolean) {
  if (eq(h.entries[h.idx], loc)) {
    return;
  }
  h.entries.splice(h.idx + 1, h.entries.length, loc);
  h.idx++;
  if (h.entries.length > maxEntries) {
    let n = h.entries.length - maxEntries;
    h.entries.splice(0, n);
    h.idx -= n;
  }
}

export function navCanBack<T>(h: NavHistory<T>): boolean {
  return h.idx > 0;
}

export function navCanForward<T>(h: NavHistory<T>): boolean {
  return h.idx < h.entries.length - 1;
}

// Moves back and returns the place to show, or null at the start.
export function navBack<T>(h: NavHistory<T>): T | null {
  if (!navCanBack(h)) {
    return null;
  }
  h.idx--;
  return h.entries[h.idx];
}

// Moves forward and returns the place to show, or null at the end.
export function navForward<T>(h: NavHistory<T>): T | null {
  if (!navCanForward(h)) {
    return null;
  }
  h.idx++;
  return h.entries[h.idx];
}
